/**
 * BIC Completeness Monitor — Phase 4 contract (Issue #1557).
 *
 * Defines the canonical contract that:
 *
 *   1. Every owning service implements via `GetBicCompleteness(business_id)` —
 *      returns a {@link BicServiceFreshness} listing per-table totals (rows at
 *      current BIC version vs older vs unstamped vs tenant-edited).
 *   2. The 19-control-tower-service aggregator fans out to every owning
 *      service, sums their {@link BicServiceFreshness} into one
 *      {@link BicCompletenessReport}, and computes a single 0-100 freshness
 *      score per business.
 *   3. The admin portal displays alongside the businesses list and offers a
 *      drill-down panel showing where each tenant is stuck on an older BIC
 *      version.
 *
 * ### Score formula
 *
 * The score is the % of *bootstrap-managed* rows that are stamped at the
 * current BIC manifest version. Tenant-edited rows are excluded from the
 * denominator — they're owned by the tenant, not BIC, and never need to
 * "upgrade".
 *
 *     managedRows = sum(table.totalRows - table.tenantEdited)
 *     atCurrent   = sum(table.atCurrentVersion)
 *     score       = managedRows === 0 ? 0 : round(atCurrent / managedRows * 100)
 *
 * ### Error handling
 *
 * The aggregator is resilient: if one service fan-out fails, that service's
 * {@link BicServiceFreshness.tables} is returned as `[]` and the report's
 * `partial` flag is set. The score is computed only over services that
 * responded successfully.
 *
 * @see Issue #1557 — Phase 4 BIC completeness monitor
 */

/**
 * Per-service per-table BIC freshness counts for one (service, table, businessId)
 * triple.
 *
 * The five count buckets are mutually exclusive:
 *
 *   atCurrentVersion + atOlderVersion + unstamped = totalRows
 *
 * `tenantEdited` is an *additional* dimension — a row can be both
 * `atCurrentVersion` and `tenantEdited`, or both `unstamped` and
 * `tenantEdited`. The aggregator subtracts `tenantEdited` from the
 * denominator to compute a fair score.
 */
export interface BicTableFreshness {
  /** Table name — matches the Prisma `@@map(...)` on the owning model. */
  table: string;
  /** Total per-tenant rows for this business. */
  totalRows: number;
  /** Rows where `bic_version_at_seed === BIC_MANIFEST_VERSION`. */
  atCurrentVersion: number;
  /** Rows where `bic_version_at_seed` is set but != current version. */
  atOlderVersion: number;
  /** Rows where `bic_version_at_seed IS NULL` (legacy / tenant-defined). */
  unstamped: number;
  /**
   * Rows whose `updated_by` is NOT in the bootstrap-owned sentinel set
   * (`'seed'`, `'bic-bootstrap'`). These rows are tenant-edited and are
   * excluded from the denominator when computing the freshness score.
   */
  tenantEdited: number;
}

/**
 * Per-service BIC freshness — one entry per owning service.
 *
 * Each owning service implements `GetBicCompleteness(business_id)` to produce
 * one of these. `tables` lists every BIC-managed table the service owns.
 * `bicVersion` is informational — the version this service was built against
 * (typically `BIC_MANIFEST_VERSION` from `@ops-matrix/shared-types/seed/bic`).
 */
export interface BicServiceFreshness {
  /** Service identifier — kebab-case, matches the project name suffix. */
  service: string;
  /** Per-table breakdown for the BIC-managed tables this service owns. */
  tables: BicTableFreshness[];
  /** Current BIC manifest version this service was built against. */
  bicVersion: string;
}

/**
 * Aggregated cross-service BIC freshness for one business.
 *
 * Computed by the 19-control-tower-service aggregator from per-service
 * {@link BicServiceFreshness} rows. The score is in the 0-100 range and
 * represents the % of *bootstrap-managed* rows stamped at the current BIC
 * manifest version. Tenant-edited rows are excluded from the denominator.
 *
 * `partial` is `true` if any of the per-service fan-outs failed; the score
 * is still meaningful but is computed only over services that responded.
 * The failing services appear in `services` with `tables: []`.
 */
export interface BicCompletenessReport {
  businessId: string;
  /** Current BIC manifest version (informational, for the UI). */
  currentBicVersion: string;
  /** 0-100 — % of bootstrap-managed rows stamped at current version. */
  freshnessScore: number;
  /** Per-service breakdown. */
  services: BicServiceFreshness[];
  /**
   * `true` when at least one per-service fan-out failed. The score is still
   * computed (over the responding services), but the UI should mark the
   * report as partial so an operator knows to retry or investigate.
   */
  partial: boolean;
  /** UTC ISO timestamp when the report was generated. */
  generatedAt: string;
}

/**
 * `updated_by` sentinel set that bootstrap is allowed to overwrite. Anything
 * else is treated as a tenant edit (for the purpose of the completeness
 * monitor's `tenantEdited` count). Mirrors the same set used by every
 * service's BIC bootstrap.
 *
 * Exported here so per-service `GetBicCompleteness` implementations all
 * agree on the definition of "tenant-edited".
 */
export const BIC_BOOTSTRAP_OWNED_UPDATERS: ReadonlySet<string> = new Set(['seed', 'bic-bootstrap']);

/**
 * SQL `IN (...)` literal derived from {@link BIC_BOOTSTRAP_OWNED_UPDATERS}.
 * Used by every owning service's `GetBicCompleteness` raw-SQL query so the
 * sentinel set stays in sync with the shared constant — adding a value to
 * the set propagates to all 9 services at build time.
 *
 * Safe for SQL string interpolation: the values are hardcoded string
 * literals from a closed set, not user input. The module-load-time guard
 * below rejects values containing single-quotes so an apostrophe-bearing
 * sentinel can never produce broken SQL.
 *
 * NULL semantic: completeness queries pair this constant with a `FILTER
 * (WHERE updated_by NOT IN (...))` clause. PostgreSQL's `NOT IN` returns
 * NULL (not TRUE) for `updated_by IS NULL`, so legacy rows with no stamp
 * fall OUT of the `tenant_edited` count — which is correct: a NULL-stamp
 * row is unclaimed (legacy), NOT tenant-edited. Combined with `unstamped`
 * counting the same row toward the denominator, this drags the freshness
 * score DOWN until bootstrap claims the row by writing `'bic-bootstrap'`.
 * This semantic must NOT be "fixed" by adding `OR updated_by IS NULL` —
 * doing so would inflate `tenant_edited`, exclude the row from the score
 * entirely, and silently mask unmanaged legacy rows from operators.
 *
 * Example:
 *   `COUNT(*) FILTER (WHERE updated_by NOT IN (${BIC_OWNED_UPDATERS_SQL})) AS tenant_edited`
 */
export const BIC_OWNED_UPDATERS_SQL: string = (() => {
  for (const v of BIC_BOOTSTRAP_OWNED_UPDATERS) {
    if (v.includes("'")) {
      throw new Error(
        `BIC_BOOTSTRAP_OWNED_UPDATERS sentinel "${v}" contains a single quote — would produce broken SQL when interpolated. Pick a sentinel without an apostrophe.`,
      );
    }
  }
  return Array.from(BIC_BOOTSTRAP_OWNED_UPDATERS)
    .map((v) => `'${v}'`)
    .join(',');
})();

/**
 * Raw Postgres row shape returned by the BIC conditional-aggregation query
 * in every owning service's `GetBicCompleteness` implementation. Five
 * mutually-exclusive count buckets plus the orthogonal `tenant_edited`
 * dimension.
 *
 * `bigint` because Postgres `COUNT(*)` returns `int8` and Prisma's
 * `$queryRawUnsafe` preserves the native type. Per-service implementations
 * convert to `Number` at the boundary when populating the camelCase
 * {@link BicTableFreshness} contract.
 */
export interface BicRawTableCount {
  total: bigint;
  at_current: bigint;
  at_older: bigint;
  unstamped: bigint;
  tenant_edited: bigint;
}

/**
 * Compute the freshness score (0-100) from a list of per-service freshness
 * results. Excludes tenant-edited rows from the denominator. Returns 0 when
 * there are zero managed rows — never NaN.
 *
 * Pure function. Used by the aggregator and by tests.
 */
export function computeBicFreshnessScore(services: ReadonlyArray<BicServiceFreshness>): number {
  let managedRows = 0;
  let atCurrent = 0;

  for (const svc of services) {
    for (const table of svc.tables) {
      managedRows += Math.max(0, table.totalRows - table.tenantEdited);
      atCurrent += table.atCurrentVersion;
    }
  }

  if (managedRows === 0) return 0;
  // Cap at 100 (defensive: in case of double-counting bugs upstream)
  return Math.min(100, Math.round((atCurrent / managedRows) * 100));
}
